import styled from 'styled-components'
import Colors from 'Theme/Colors'
import Styles from './DefaultRouter.module.css'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import {
  StationsPage,
  OperatorUserPage,
  StationsMapPage,
  ChargingTokensPage,
  OperatorUserProfile,
  OperatorAlertsPage,
  AnalyticsPage,
  TariffPage,
} from 'Containers'
import { connect } from 'react-redux'
import AuthSelectors from 'Stores/Auth/Selectors'
import AuthActions from 'Stores/Auth/Actions'
import InfinityEnums from 'Enums/InfinityEnums'
import { SideNavBar, IFAvatar, NotFound } from 'Components'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { changeRsuiteColors } from 'Theme'
import RequestState from 'Enums/RequestState'

const PageContainer = styled.div`
  background-color: ${(props) => props.background};
`
const DefaultRouter = ({
  user,
  operator,
  fetchOperatorUser,
  fetchOperatorUserRequestState,
}) => {
  // theme with state to implement darkmode
  const [theme, setTheme] = useState(
    createTheme({
      palette: {
        primary: {
          main: operator.primaryColor,
        },
      },
    }),
  )

  useEffect(() => {
    fetchOperatorUser()
  }, [])

  const OperatorColors = Colors
  OperatorColors.primary = operator.primaryColor
  OperatorColors.StationListHeaderBackground =
    operator.primaryColor + Colors.HexTransparent.T10
  OperatorColors.StationListItemBackgroundHover =
    operator.primaryColor + Colors.HexTransparent.T10
  OperatorColors.StationListItemSelected =
    operator.primaryColor + Colors.HexTransparent.T20
  OperatorColors.TabsBackground =
    operator.primaryColor + Colors.HexTransparent.T10
  OperatorColors.FilterBackground =
    operator.primaryColor + Colors.HexTransparent.T10
  OperatorColors.ChargePointLogListItemHover =
    operator.primaryColor + Colors.HexTransparent.T10
  changeRsuiteColors(operator.primaryColor)

  const requiredPermissions = [
    InfinityEnums.OperatorUserPermission.CAN_VIEW_STATION_CHARGEPOINT,
    InfinityEnums.OperatorUserPermission.CAN_VIEW_CHARGING_TOKEN,
    InfinityEnums.OperatorUserPermission.CAN_VIEW_OPERATOR_USER,
  ]

  const hasAnyPermission = user?.permissions?.some((permission) =>
    requiredPermissions.includes(permission),
  )

  const navigate = useNavigate()
  useEffect(() => {
    if (
      fetchOperatorUserRequestState === RequestState.SUCCEEDED &&
      !hasAnyPermission
    ) {
      navigate('/operatorUserProfile')
    }
  }, [fetchOperatorUserRequestState, hasAnyPermission, navigate])
  return (
    <ThemeProvider theme={theme}>
      <PageContainer
        background={Colors.background}
        className={Styles.PageContainer}
      >
        <SideNavBar
          avatar={<IFAvatar name={user.name} size={32} />}
          permissions={user.permissions}
          logoUrl={operator.logoUrl}
        />
        <div className={Styles.RightColumnContainer}>
          <div className={Styles.ContentContainer}>
            <Routes>
              <Route
                path="/"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_STATION_CHARGEPOINT,
                  ) ? (
                    <Navigate to="/sites" />
                  ) : user.permissions.includes(
                      InfinityEnums.OperatorUserPermission
                        .CAN_VIEW_CHARGING_TOKEN,
                    ) ? (
                    <Navigate to="/cards" />
                  ) : user.permissions.includes(
                      InfinityEnums.OperatorUserPermission
                        .CAN_VIEW_OPERATOR_USER,
                    ) ? (
                    <Navigate to="/operatorUsers" />
                  ) : user.permissions.includes(
                      InfinityEnums.OperatorUserPermission.CAN_VIEW_ALERT,
                    ) ? (
                    <Navigate to="/operatorAlerts" />
                  ) : user.permissions.includes(
                      InfinityEnums.OperatorUserPermission
                        .CAN_VIEW_ANALYTICS_DATA,
                    ) ? (
                    <Navigate to="/analyticsData" />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/sites/"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_STATION_CHARGEPOINT,
                  ) ? (
                    <StationsPage username={user.name} />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/sites/:siteId"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_STATION_CHARGEPOINT,
                  ) ? (
                    <StationsPage username={user.name} />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/sites/:siteId/stations/:stationid"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_STATION_CHARGEPOINT,
                  ) ? (
                    <StationsPage username={user.name} />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/cards"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_CHARGING_TOKEN,
                  ) ? (
                    <ChargingTokensPage />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/cards/:cardId"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_CHARGING_TOKEN,
                  ) ? (
                    <ChargingTokensPage />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/operatorUsers"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission.CAN_VIEW_OPERATOR_USER,
                  ) ? (
                    <OperatorUserPage />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/map"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_STATION_CHARGEPOINT,
                  ) ? (
                    <StationsMapPage />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/operatorUserProfile"
                element={<OperatorUserProfile />}
              />
              <Route
                path="/operatorAlerts"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission.CAN_VIEW_ALERT,
                  ) ? (
                    <OperatorAlertsPage />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/analyticsData"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission
                      .CAN_VIEW_ANALYTICS_DATA,
                  ) ? (
                    <AnalyticsPage />
                  ) : (
                    fetchOperatorUserRequestState ===
                      RequestState.SUCCEEDED && <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/tariffs"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission.CAN_VIEW_TARIFF,
                  ) ? (
                    <TariffPage />
                  ) : (
                    <Navigate to="/NotFound" />
                  )
                }
              />
              <Route
                path="/tariffs/:tariffId"
                element={
                  user.permissions.includes(
                    InfinityEnums.OperatorUserPermission.CAN_VIEW_TARIFF,
                  ) ? (
                    <TariffPage />
                  ) : (
                    <Navigate to="/NotFound" />
                  )
                }
              />

              <Route
                path="*"
                element={
                  fetchOperatorUserRequestState === RequestState.SUCCEEDED &&
                  !hasAnyPermission ? (
                    <OperatorUserProfile />
                  ) : (
                    <NotFound />
                  )
                }
              />
            </Routes>
          </div>
        </div>
      </PageContainer>
    </ThemeProvider>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchOperatorUser: () => dispatch(AuthActions.fetchOperatorUser()),
  }
}

const mapStateToProps = (state) => ({
  user: AuthSelectors.getOperatorUser(state),
  operator: AuthSelectors.getOperator(state),
  fetchOperatorUserRequestState:
    AuthSelectors.getFetchOperatorUserRequestState(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(DefaultRouter)
